import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { WORK_TYPES, BUDGETS } from "@/lib/format";
import { Btn, Chip, Field, Logo } from "@/components/rp";

export default function LeadForm() {
  const { user } = useAuth();
  const [f, setF] = useState({
    name: user?.role === "client" ? user.name : "", email: user?.role === "client" ? user.email : "",
    phone: user?.role === "client" ? user.phone : "", work_type: "", sqm: "", address: "", budget: "", description: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target ? e.target.value : e });
  const back = user ? (user.role === "admin" ? "/admin" : "/profilo") : "/login";

  const submit = async (e) => {
    e.preventDefault();
    if (!f.work_type) return toast.error("Seleziona il tipo di intervento");
    setLoading(true);
    try {
      await api.post("/leads", f);
      setSent(true);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-[#EBEBEB] px-6 sm:px-10 h-16 flex items-center justify-between">
        <Logo className="h-7" />
        <Link to={back} className="flex items-center gap-2 text-sm text-[#757575] hover:text-black" data-testid="lead-back-link"><ArrowLeft className="h-4 w-4" /> Indietro</Link>
      </header>
      <div className="max-w-2xl mx-auto px-6 py-12 sm:py-16">
        {sent ? (
          <div className="rp-card p-10 text-center rp-fade" data-testid="lead-success">
            <CheckCircle2 className="h-10 w-10 text-[#198754] mx-auto" strokeWidth={1.5} />
            <h1 className="text-3xl font-semibold tracking-tight mt-4">Richiesta inviata</h1>
            <p className="text-[#757575] mt-3">Grazie {f.name.split(" ")[0]}! Ti contatteremo a breve per fissare il sopralluogo gratuito.</p>
            <Link to={back}><Btn className="mt-8" data-testid="lead-success-back">Torna indietro</Btn></Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-8 rp-fade" data-testid="lead-form">
            <div>
              <p className="rp-label mb-3">Preventivo gratuito</p>
              <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight">Raccontaci il tuo progetto</h1>
              <p className="text-[#757575] mt-3">Ti ricontattiamo per un sopralluogo gratuito e un preventivo dettagliato.</p>
            </div>
            <div className="space-y-3">
              <p className="rp-label">Tipo di intervento</p>
              <div className="flex flex-wrap gap-2">
                {WORK_TYPES.map((w, i) => <Chip key={w} active={f.work_type === w} onClick={() => set("work_type")(w)} data-testid={`lead-work-type-${i}`}>{w}</Chip>)}
              </div>
            </div>
            <div className="space-y-3">
              <p className="rp-label">Budget indicativo</p>
              <div className="flex flex-wrap gap-2">
                {BUDGETS.map((b, i) => <Chip key={b} active={f.budget === b} onClick={() => set("budget")(b)} data-testid={`lead-budget-${i}`}>{b}</Chip>)}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Nome e cognome"><input required value={f.name} onChange={set("name")} className="rp-field" data-testid="lead-name-input" /></Field>
              <Field label="Telefono"><input value={f.phone} onChange={set("phone")} className="rp-field" type="tel" data-testid="lead-phone-input" /></Field>
              <Field label="Email"><input value={f.email} onChange={set("email")} className="rp-field" type="email" data-testid="lead-email-input" /></Field>
              <Field label="Metri quadri"><input value={f.sqm} onChange={set("sqm")} className="rp-field" inputMode="numeric" placeholder="es. 80" data-testid="lead-sqm-input" /></Field>
            </div>
            <Field label="Indirizzo dell'immobile"><input value={f.address} onChange={set("address")} className="rp-field" placeholder="Via, numero, città" data-testid="lead-address-input" /></Field>
            <Field label="Descrizione"><textarea rows={4} value={f.description} onChange={set("description")} className="rp-field" placeholder="Cosa vorresti realizzare?" data-testid="lead-description-input" /></Field>
            <Btn type="submit" loading={loading} className="w-full sm:w-auto h-12 px-10" data-testid="lead-submit-button">Invia richiesta</Btn>
          </form>
        )}
      </div>
    </div>
  );
}
